import Lenis from "lenis";
import { MOTION_CONFIG, isMobileViewport } from "./motion-config.js";
import { getFooterScrollTop, initFooterReveal } from "./footer.js";
import { initMainHomeAbout } from "./main-home.js";
import {
  initMainHomeAdvantage,
  initMainHomeHero,
  initMainAboutBlocks,
  initMainValueReveal,
  initMainStoryReveal,
  initMainHeadingsReveal,
  initMainContactReveal,
  initMainPfSwiper,
  initMainPfReveal,
  initMainPf2Reveal,
  initMainMagazineSlide,
} from "./main.js";
import { initSubPageInteractions } from "./sub.js";
import { initContactCollage } from "./component/ContactCollage.js";
import { initContactEstimate } from "./component/contact.js";
import { initNewsroom } from "./component/newsroom.js";
import { initPortfolio } from "./component/portfolio.js";
import { initCountRoll } from "./modules/CountRoll.js";
import { initProcessAccordion } from "./modules/ProcessAccordion.js";
import { initServiceAccordion } from "./modules/ServiceAccordion.js";
import { initWhyStack } from "./modules/WhyStack.js";
// 헤더(GNB 호버/LNB/드로어/스크롤 상태) 로직. self-invoking IIFE라 side-effect import로 실행된다.
import "./header.js";

document.documentElement.classList.add("js");

function initApp() {
  resetScrollToTop();
  gsap.registerPlugin(ScrollTrigger);
  // 모바일 주소창이 스크롤 중 나타났다/사라지며 일으키는 viewport resize 로
  // 핀(why-stack 등) spacer 높이가 재계산돼 "높이가 확 튀는" 현상을 막는다.
  ScrollTrigger.config({ ignoreMobileResize: true });
  initLenis();
  initCharHoverBtns();

  if (window.AOS && typeof window.AOS.init === "function") {
    window.AOS.init({
      duration: 520,
      easing: "ease-out-quart",
      once: true,
      offset: 0,
    });
  }

  initMainHomeHero();
  initMainHomeAbout();
  initMainAboutBlocks();
  initMainHomeAdvantage();
  initMainPfSwiper();
  initMainMagazineSlide();
  initFloatingButton();
  initMarquee();
  initSubPageInteractions({ lockGlobalScroll, unlockGlobalScroll });
  initContactEstimate();
  initCountRoll();
  initProcessAccordion();
  // 서비스 페이지: 스텝 아코디언을 서비스 전용 셀렉터로 재사용
  // (PERFORMANCE 는 .no-main--count 마크업을 써서 기본 initCountRoll() 가 처리)
  initProcessAccordion({
    root: ".service-steps-wrap",
    panelSel: ".service-steps__panel",
    triggerSel: ".service-steps__trigger",
  });
  initServiceAccordion();
  initWhyStack();
  initContactCollage();
  initNewsroom();
  initPortfolio();
  initFooterReveal();

  // 텍스트/이미지 리빌은 핀(about, why)·아코디언 등 레이아웃 높이를 바꾸는
  // 인터랙션이 모두 생성된 "뒤"에 만들어야 ScrollTrigger 위치가 정확히 잡힌다.
  initMainValueReveal();
  initMainStoryReveal();
  initMainPfReveal();
  initMainPf2Reveal();
  initMainHeadingsReveal();
  initMainContactReveal();

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.__lenis?.resize?.();
      ScrollTrigger.refresh();
      alignScrollToLocationHashIfNeeded();
    });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initApp, { once: true });
} else {
  initApp();
}

function resolveHashAnchorTarget() {
  const raw = window.location.hash.replace(/^#/, "");
  if (!raw) {
    return null;
  }
  let id = raw;
  try {
    id = decodeURIComponent(raw);
  } catch {
    return null;
  }
  if (!id) {
    return null;
  }
  const el = document.getElementById(id);
  return el instanceof HTMLElement ? el : null;
}

function alignScrollToLocationHashIfNeeded() {
  const el = resolveHashAnchorTarget();
  if (!el) {
    return;
  }

  const run = () => {
    const header = document.getElementById("main-header");
    const offset = header instanceof HTMLElement ? header.offsetHeight : 0;

    if (window.__lenis) {
      window.__lenis.scrollTo(el, {
        offset: -offset,
        immediate: true,
        force: true,
      });
      return;
    }

    const top =
      el.getBoundingClientRect().top +
      (window.scrollY || document.documentElement.scrollTop) -
      offset;
    window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
  };

  requestAnimationFrame(run);

  window.addEventListener(
    "load",
    () => {
      window.__lenis?.resize?.();
      ScrollTrigger.refresh();
      run();
    },
    { once: true },
  );
}

function resetScrollToTop() {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  const navigationEntry = performance.getEntriesByType("navigation")[0];
  const shouldForceTop =
    !navigationEntry ||
    navigationEntry.type === "navigate" ||
    navigationEntry.type === "reload";

  if (!shouldForceTop) {
    return;
  }

  if (resolveHashAnchorTarget()) {
    return;
  }

  const applyTop = () => {
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo?.(0, { immediate: true, force: true });
  };

  applyTop();

  window.addEventListener(
    "load",
    () => {
      const scrolled =
        window.__lenis?.scroll ??
        window.scrollY ??
        document.documentElement.scrollTop ??
        0;
      if (scrolled < 50) {
        applyTop();
      }
    },
    { once: true },
  );

  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) {
      const scrolled =
        window.scrollY ?? document.documentElement.scrollTop ?? 0;
      if (scrolled < 50) {
        applyTop();
      }
    }
  });
}

function initLenis() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return null;
  }

  // 화면 분기에 맞는 Lenis 옵션 (모바일은 syncTouch 로 강한 플릭 흡수)
  const lenisOptions = isMobileViewport()
    ? MOTION_CONFIG.lenis.mobile
    : MOTION_CONFIG.lenis.desktop;

  const lenis = new Lenis({
    infinite: false,
    ...lenisOptions,
  });

  lenis.on("scroll", () => {
    ScrollTrigger.update();
    window.__syncHeaderScrollState?.();
  });

  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });
  gsap.ticker.lagSmoothing(0);

  // Lenis 는 실제 스크롤과 GSAP 측정이 어긋날 수 있어 pin/핀 스페이싱(높이)이 튀는 원인이 된다.
  // scroll 위치 get/set 을 lenis 에 맞추면 ScrollTrigger 핀 거리·progress 가 안정화된다.
  const scrollRoot = document.scrollingElement || document.documentElement;
  ScrollTrigger.scrollerProxy(scrollRoot, {
    scrollTop(value) {
      if (arguments.length) {
        lenis.scrollTo(value, { immediate: true, force: true });
      }
      return lenis.scroll;
    },
    getBoundingClientRect() {
      return {
        top: 0,
        left: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      };
    },
    pinType: "fixed",
  });
  ScrollTrigger.defaults({ scroller: scrollRoot });

  ScrollTrigger.addEventListener("refresh", () => {
    lenis.resize();
  });

  window.addEventListener(
    "load",
    () => {
      lenis.resize();
      ScrollTrigger.refresh();
    },
    { once: true },
  );

  window.__lenis = lenis;
  window.__syncHeaderScrollState?.();

  // 데스크톱↔모바일 분기를 넘으면 scrub/pin 길이가 다른 프리셋으로 재초기화 되도록 한 번만 새로고침.
  // (scrub 값은 ScrollTrigger 생성 시 결정되므로 동적 변경이 아닌 reload 가 가장 깔끔하다.)
  const breakpointMql = window.matchMedia(`(max-width: 768px)`);
  let breakpointInitial = breakpointMql.matches;
  const onBreakpointFlip = () => {
    if (breakpointMql.matches !== breakpointInitial) {
      breakpointInitial = breakpointMql.matches;
      window.location.reload();
    }
  };
  if (typeof breakpointMql.addEventListener === "function") {
    breakpointMql.addEventListener("change", onBreakpointFlip);
  } else if (typeof breakpointMql.addListener === "function") {
    breakpointMql.addListener(onBreakpointFlip);
  }

  return lenis;
}

const globalScrollLocks = new Set();

function syncGlobalScrollLockState() {
  const shouldLock = globalScrollLocks.size > 0;

  document.documentElement.classList.toggle("scroll-lock", shouldLock);
  document.body.classList.toggle("scroll-lock", shouldLock);
  document.body.classList.toggle("--hidden", shouldLock);
  document.body.style.overflow = shouldLock ? "hidden" : "";

  if (shouldLock) {
    window.__lenis?.stop?.();
    return;
  }

  window.__lenis?.start?.();
}

function lockGlobalScroll(key) {
  if (!key) return;
  globalScrollLocks.add(key);
  syncGlobalScrollLockState();
}

function unlockGlobalScroll(key) {
  if (!key) return;
  globalScrollLocks.delete(key);
  syncGlobalScrollLockState();
}

window.__lockGlobalScroll = lockGlobalScroll;
window.__unlockGlobalScroll = unlockGlobalScroll;

function initFloatingButton() {
  const floatingButton = document.querySelector(".no-floating-button");
  if (!floatingButton) return;

  const topButton = floatingButton.querySelector(".no-floating-button__top");
  if (!topButton) return;

  topButton.addEventListener("click", () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  });

  const handleScroll = () => {
    const scrollY = window.scrollY || window.pageYOffset;
    const threshold = 300;

    const footerTop = getFooterScrollTop();

    if (footerTop !== null) {
      const buttonHeight = topButton.offsetHeight || 56;
      const buttonBottom = scrollY + window.innerHeight - buttonHeight - 20;

      if (buttonBottom >= footerTop) {
        topButton.classList.remove("is-visible");
        return;
      }
    }

    if (scrollY > threshold) {
      topButton.classList.add("is-visible");
    } else {
      topButton.classList.remove("is-visible");
    }
  };

  window.addEventListener("scroll", handleScroll, { passive: true });

  handleScroll();
}

function initMarquee() {
  const marquees = document.querySelectorAll("[data-marquee]");
  if (!marquees.length) return;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  marquees.forEach((el) => {
    if (el.dataset.marqueeInitialized === "1") return;
    el.dataset.marqueeInitialized = "1";

    const track = el.querySelector(".no-marquee__track");
    if (!track) return;

    const speed = parseFloat(el.dataset.speed) || 28;
    const isReverse = el.dataset.direction === "reverse";

    if (isReverse) {
      gsap.fromTo(
        track,
        { xPercent: -50 },
        { xPercent: 0, ease: "none", duration: speed, repeat: -1 },
      );
    } else {
      gsap.to(track, {
        xPercent: -50,
        ease: "none",
        duration: speed,
        repeat: -1,
      });
    }
  });
}

function initCharHoverBtns() {
  document.querySelectorAll(".no-btn-char").forEach((btn) => {
    if (btn.querySelector(".no-btn-char__cell")) return;

    const icon = btn.querySelector(".no-btn-char__icon");
    const raw = [...btn.childNodes]
      .filter((node) => node !== icon)
      .map((node) => node.textContent)
      .join("")
      .trim();

    if (!raw) return;

    btn.setAttribute("aria-label", raw);

    // Build char cells
    const textWrap = document.createElement("span");
    textWrap.className = "no-btn-char__text";

    [...raw].forEach((ch) => {
      const cell = document.createElement("span");
      cell.className = "no-btn-char__cell";

      const orig = document.createElement("span");
      orig.className = "no-btn-char__char no-btn-char__char--orig";
      orig.textContent = ch === " " ? "\u00A0" : ch;

      const clone = document.createElement("span");
      clone.className = "no-btn-char__char no-btn-char__char--clone";
      clone.textContent = ch === " " ? "\u00A0" : ch;

      cell.appendChild(orig);
      cell.appendChild(clone);
      textWrap.appendChild(cell);
    });

    btn.replaceChildren(textWrap);

    if (icon) {
      btn.appendChild(icon);
    }

    const origChars = textWrap.querySelectorAll(".no-btn-char__char--orig");
    const cloneChars = textWrap.querySelectorAll(".no-btn-char__char--clone");

    const tl = gsap
      .timeline({ paused: true })
      .to(
        origChars,
        {
          y: "-100%",
          duration: 0.46,
          stagger: 0.03,
          ease: "power3.inOut",
        },
        0,
      )
      .to(
        cloneChars,
        {
          y: "0%",
          duration: 0.46,
          stagger: 0.03,
          ease: "power3.inOut",
        },
        0,
      );

    btn.addEventListener("mouseenter", () => {
      tl.play();
    });

    btn.addEventListener("mouseleave", () => {
      tl.reverse();
    });
  });
}
